<template>
  <Modal>
    <Modal.Title>Confirm report creation</Modal.Title>
    <Modal.Body>
      <p class="text-[14px] leading-[22px] text-gray-800">
        You will use 1 report credit creating this report. Would you like to
        continue?
      </p>
      <Alert v-if="error" variant="error" :icon="AlertIcon" class="mt-4">
        Something went wrong creating your report. Please try again.
      </Alert>
    </Modal.Body>
    <Modal.Footer>
      <Button
        variant="stroke"
        :size="isMobile ? 'lg' : 'md'"
        @click="closeModal()"
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        :size="isMobile ? 'lg' : 'md'"
        :loading="loading"
        @click="handleConfirm"
      >
        Yes, create report
      </Button>
    </Modal.Footer>
  </Modal>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { Modal, Button, Alert } from '@dacr/library'
import { AlertIcon } from '@dacr/icons'

import { graphql } from '@/shared/graphql/router'
import { useDevice } from '@/shared/composables/useDevice'
import { useUserStore } from '@/shared/stores/user'
import { useInfringementProcessingStore } from '@/features/infringement/store'
import { closeModal, closeAllModals } from '@/shared/dialogs/modals'
import { closeAllSheets } from '@/shared/dialogs/sheets'
import type { FlaggedInfringement } from './FlaggedMatch/useFlaggedMatch'

const CREATE_REPORT_MUTATION = graphql(`
  mutation CreateReport($input: CreateReportInput!) {
    createReport(input: $input) {
      success
      error {
        code
        message
      }
    }
  }
`)

const props = defineProps<{ infringement: FlaggedInfringement }>()

const { isMobile } = useDevice()
const userStore = useUserStore()
const processingStore = useInfringementProcessingStore()

const { mutate: createReport, loading } = useMutation(CREATE_REPORT_MUTATION, {
  clientId: 'router',
})

const error = ref(false)

const handleConfirm = async () => {
  error.value = false

  try {
    const res = await createReport({
      input: {
        copyrightId: props.infringement.copyrights[0].id,
        comparingId: props.infringement.infringingFile.id,
      },
    })

    if (res?.data?.createReport.success) {
      processingStore.addExistingProcessingReport(props.infringement)
      userStore.updateUser({ isCreatingReport: true })
      closeAllModals()
      return closeAllSheets()
    }

    error.value = true
  } catch {
    error.value = true
  }
}
</script>
